import { useCallback, useMemo, useRef } from 'react';

import { SalesChannelType } from '@fd/customHooks/useQueryRMSSalesChannels';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';

import {
  getIntegratedStores,
  getMarketplaceProperties,
  getUberEatsStores,
} from './marketplaceIntegrations.services';
import {
  buildManageStoreDetails,
  getMarketplaceListUrl,
  getMarketplaceManagePageUrl,
  isMarketplaceMfeLoaded,
  openWithFullPageLoad,
  writeManageStore,
} from './marketplaceManage';

export const getMarketplaceManageStoreKey = 'getMarketplaceManageStore';

const STORE_FRESH_FOR_MS = 30_000;

const getMarketplaceManageStoreQuery = (
  orgId: string,
  salesChannelId: string,
  salesChannelType: string
) => ({
  queryKey: [getMarketplaceManageStoreKey, orgId, salesChannelId, salesChannelType],
  queryFn: async () => {
    const [integratedStores, properties, uberEatsStores] = await Promise.all([
      getIntegratedStores(orgId),
      getMarketplaceProperties(orgId),
      salesChannelType === SalesChannelType.UBER_EATS
        ? getUberEatsStores(orgId)
        : Promise.resolve([]),
    ]);

    const store = integratedStores.find(
      (integratedStore) =>
        integratedStore.salesChannelId === salesChannelId &&
        integratedStore.marketplace === salesChannelType
    );
    return store ? buildManageStoreDetails({ store, properties, uberEatsStores }) : null;
  },
  retry: false,
});

/**
 * Prefetches the connected store behind a sales channel and returns a click handler that does what
 * the marketplace-integrations table's Manage action does: hands the store to the MFE's manage
 * store and opens its manage page. Channels without a connected store open the marketplace's table.
 */
export const useMarketplaceManage = ({
  appId,
  orgId,
  salesChannelId,
  salesChannelType,
  enabled,
}: {
  appId: string;
  orgId: string;
  salesChannelId: string;
  salesChannelType: string;
  enabled: boolean;
}) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const query = useMemo(
    () => getMarketplaceManageStoreQuery(orgId, salesChannelId, salesChannelType),
    [orgId, salesChannelId, salesChannelType]
  );
  const isOpening = useRef(false);

  useQuery({ ...query, enabled: enabled && !!orgId && !!salesChannelId });

  return useCallback(async () => {
    // Ignore repeat clicks while the store is still loading
    if (isOpening.current) {
      return;
    }
    isOpening.current = true;

    try {
      // Reuses a recent prefetch or joins its in-flight request; anything older is refetched
      const storeDetails = await queryClient
        .fetchQuery({ ...query, staleTime: STORE_FRESH_FOR_MS })
        .catch(() => null);
      const managePageUrl =
        storeDetails && getMarketplaceManagePageUrl(appId, salesChannelType, storeDetails.id);
      if (!storeDetails || !managePageUrl) {
        history.push(getMarketplaceListUrl(appId, salesChannelType));
        return;
      }

      writeManageStore(storeDetails);
      if (isMarketplaceMfeLoaded()) {
        openWithFullPageLoad(managePageUrl);
      } else {
        history.push(managePageUrl);
      }
    } finally {
      isOpening.current = false;
    }
  }, [appId, salesChannelType, query, history, queryClient]);
};
